"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "motion/react";
import type { MenuLink, MenuPanel, SiteMenu } from "../../lib/menu";

const EASE = [0.2, 0.8, 0.2, 1] as const;

function Chevron() {
  return (
    <svg className="mn-chev" viewBox="0 0 10 6" width="11" height="7" aria-hidden="true">
      <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep";

/**
 * Hamburger + left-hand drawer for viewports below `md`. Mirrors the desktop
 * menu tree: MEN / WOMEN / BRANDS / NEW IN as accordion rows, nested
 * accordions for Tops and Bottoms, a tappable feature tile at the end of each
 * section, and the utility links at the bottom.
 */
export default function MobileNav({ menu }: { menu: SiteMenu }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();

  // Close when the route changes.
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    setOpen(false);
    setSection(null);
    setSub(null);
  }

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const trigger = triggerRef.current;
    closeButtonRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
      ).filter((el) => !el.closest("[inert]"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      (previouslyFocused ?? trigger)?.focus();
    };
  }, [open]);

  function close() {
    setOpen(false);
    setSection(null);
    setSub(null);
  }

  function renderLink(l: MenuLink, panel: MenuPanel, i: number) {
    if (!l.children) {
      return (
        <li key={l.href + l.label}>
          <Link
            href={l.href}
            onClick={close}
            className={`block py-[11px] text-[12px] uppercase tracking-[0.12em] ${focusRing}`}
          >
            {l.label}
          </Link>
        </li>
      );
    }
    const id = `${panelId}-${panel.key}-sub-${i}`;
    const isOpen = sub === `${panel.key}:${l.label}`;
    return (
      <li key={l.href + l.label}>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={id}
          onClick={() => setSub(isOpen ? null : `${panel.key}:${l.label}`)}
          className={`flex w-full items-center gap-2 py-[11px] text-left text-[12px] uppercase tracking-[0.12em] ${focusRing}`}
        >
          {l.label}
          <Chevron />
        </button>
        <div id={id} className="mn-acc" data-open={isOpen} inert={!isOpen}>
          <div>
            <ul className="pb-1 pl-4">
              {l.children.map((c) => (
                <li key={c.href + c.label}>
                  <Link
                    href={c.href}
                    onClick={close}
                    className={`block py-[9px] text-[11.5px] uppercase tracking-[0.12em] text-ink/80 ${focusRing}`}
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </li>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="-ml-2 flex h-10 w-10 flex-col items-center justify-center gap-[5px] md:hidden"
      >
        <span className="h-px w-5 bg-current" />
        <span className="h-px w-5 bg-current" />
      </button>

      <LazyMotion features={domAnimation} strict>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[60] md:hidden">
            <m.button
              type="button"
              aria-label="Close menu"
              tabIndex={-1}
              onClick={close}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.2 }}
              className="absolute inset-0 bg-ink/45"
            />
            <m.div
              id={panelId}
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
              initial={{ x: reduceMotion ? 0 : "-100%", opacity: reduceMotion ? 0 : 1 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: reduceMotion ? 0 : "-100%", opacity: reduceMotion ? 0 : 1 }}
              transition={{ duration: reduceMotion ? 0 : 0.28, ease: EASE }}
              className="absolute inset-y-0 left-0 flex w-[92%] max-w-[420px] flex-col overflow-y-auto overflow-x-hidden bg-paper text-ink shadow-[24px_0_50px_-24px_rgba(10,10,10,0.35)]"
            >
              <div className="flex h-16 shrink-0 items-center justify-between px-5">
                <span className="text-[11px] uppercase tracking-[0.16em] text-ink/60">Menu</span>
                <button
                  ref={closeButtonRef}
                  type="button"
                  aria-label="Close menu"
                  onClick={close}
                  className={`-mr-2 rounded-full p-2 text-ink/80 transition hover:text-ink ${focusRing}`}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>

              <nav aria-label="Primary" className="flex-1 px-5">
                <ul className="border-t border-ink/10">
                  {menu.panels.map((p) => {
                    const isOpen = section === p.key;
                    const id = `${panelId}-${p.key}`;
                    return (
                      <li key={p.key} className="border-b border-ink/10">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={id}
                          onClick={() => {
                            setSection(isOpen ? null : p.key);
                            setSub(null);
                          }}
                          className={`flex w-full items-center justify-between py-[18px] text-left font-display text-[22px] font-extrabold uppercase tracking-[0.04em] ${focusRing}`}
                        >
                          {p.label}
                          <Chevron />
                        </button>
                        <div id={id} className="mn-acc" data-open={isOpen} inert={!isOpen}>
                          <div>
                            <ul className="pb-3">{p.links.map((l, i) => renderLink(l, p, i))}</ul>
                            <Link
                              href={p.feature.href}
                              onClick={close}
                              aria-label={`${p.feature.title}. Explore the collection`}
                              className={`relative mb-5 block aspect-[4/5] overflow-hidden ${focusRing} ${p.feature.images[0]?.fit === "cover" ? "bg-ink" : "bg-panel"}`}
                            >
                              <div className={`absolute inset-0 grid gap-1 ${p.feature.images.length > 1 ? "grid-cols-2 bg-paper" : ""}`}>
                                {p.feature.images.map((img) => (
                                  // eslint-disable-next-line @next/next/no-img-element
                                  <img
                                    key={img.src}
                                    src={img.src}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    className={`h-full w-full ${img.fit === "contain" ? "box-border object-contain px-[8%] pb-[18%] pt-[6%]" : "object-cover"}`}
                                    style={{ objectPosition: img.position }}
                                  />
                                ))}
                              </div>
                              {p.feature.images[0]?.fit === "cover" && (
                                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/60 to-transparent" />
                              )}
                              <p
                                className={`absolute inset-x-0 bottom-4 text-center text-[11px] uppercase leading-[1.75] tracking-[0.16em] ${p.feature.images[0]?.fit === "cover" ? "text-paper" : "text-ink"}`}
                              >
                                <b className="block font-bold">{p.feature.title}</b>
                                <span>Explore the collection</span>
                              </p>
                            </Link>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {menu.plain?.map((l) => (
                    <li key={l.href} className="border-b border-ink/10">
                      <Link
                        href={l.href}
                        onClick={close}
                        className={`block py-[18px] font-display text-[22px] font-extrabold uppercase tracking-[0.04em] ${focusRing}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <ul className="shrink-0 px-5 pb-8 pt-6">
                {menu.utility.map((u) => (
                  <li key={u.href}>
                    <Link
                      href={u.href}
                      onClick={close}
                      className={`block py-2 text-[11.5px] uppercase tracking-[0.14em] text-ink/70 ${focusRing}`}
                    >
                      {u.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </m.div>
          </div>
        )}
      </AnimatePresence>
      </LazyMotion>
    </>
  );
}
